import React from "react";
import { CheckCircle, Circle, Clock3, BadgeCheck } from "lucide-react";
import { toast } from "react-hot-toast";

const PackageCard = ({
  books,
  price,
  planValue,
  registration,
  deposit,
  firstMonthTotal,
  discountPercentage = 0,
  renewal,
  selected = false,
  packDescription,
  packageName,
  highlightLabel,
  scheduledSubscription,
  disabled = false,
  onSelect = () => {},
}) => {
  const hasDiscount = discountPercentage > 0;
  const selectionBlocked = scheduledSubscription && !disabled;
  const discountedPrice = hasDiscount
    ? Math.round(price - (price * discountPercentage) / 100)
    : price;

  firstMonthTotal = discountedPrice + registration + deposit;

  const bgClass = disabled
    ? highlightLabel === "Upcoming Package"
      ? "bg-green-100 border border-green-400"
      : "bg-gray-200"
    : selected
    ? "bg-primary"
    : selectionBlocked
    ? "bg-gray-100 opacity-50"
    : "bg-primary-shade1";

  const textClass = disabled
    ? "text-gray-500"
    : selected
    ? "text-white"
    : "text-text-black";

  const priceClass = disabled
    ? "text-gray-500"
    : selected
    ? "text-white"
    : "text-primary";

  return (
    <div
      title={
        scheduledSubscription && !disabled
          ? "You already have an upcoming plan scheduled."
          : ""
      }
      className={`${bgClass} ${textClass} rounded-xl shadow-md p-4 w-full h-full relative transition flex flex-col justify-between ${
        disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
      }`}
      onClick={(e) => {
        if (disabled) return;
        if (scheduledSubscription) {
          toast.error("You already have an upcoming plan scheduled.");
          return;
        }
        onSelect();
      }}
    >
      {/* Select/Deselect Button (only if not disabled) */}
      {!disabled && (
        <button
          className="absolute top-2 right-2"
          onClick={(e) => {
            e.stopPropagation();
            if (scheduledSubscription) {
              toast.error("You already have an upcoming plan scheduled.");
              return;
            }
            onSelect();
          }}
          aria-label={selected ? "Deselect package" : "Select package"}
        >
          {selected ? (
            <CheckCircle size={24} className="text-white" />
          ) : (
            <Circle size={24} className="text-gray-300" />
          )}
        </button>
      )}

      {/* Package Name */}
      <h2 className={`text-center text-xl font-bold mb-1`}>
        {packageName || `${books} Book${books > 1 ? "s" : ""}`}
      </h2>

      {/* Price Display */}
      <p className={`text-center text-2xl font-bold mb-4 ${priceClass}`}>
        {hasDiscount ? (
          <>
            <span className="line-through mr-2 text-gray-400">₹{price}</span>
            <span>₹{discountedPrice}</span>
          </>
        ) : (
          <>₹{price}</>
        )}
      </p>

      {/* Unified label for current or upcoming package */}
      {disabled && highlightLabel && (
        <div
          className={`absolute top-2 left-2 flex items-center gap-1 text-xs px-2 py-1 rounded font-medium ${
            highlightLabel === "Upcoming Package"
              ? "bg-green-600 text-white border border-green-400"
              : "bg-gray-700 text-white"
          }`}
        >
          {highlightLabel === "Upcoming Package" ? (
            <>
              <Clock3 size={14} />
              Upcoming Package
            </>
          ) : (
            <>
              <BadgeCheck size={14} />
              Current Package
            </>
          )}
        </div>
      )}

      {/* Feature List */}
      <ul className="text-sm space-y-1 mb-4 list-disc list-inside">
        {packDescription && <li>{packDescription}</li>}
        <li className="font-bold">
          {books} Book{books > 1 ? "s" : ""} at a time
        </li>
        <li>Plan Value {planValue}/-</li>
        <li>One-Time Registration {registration}/-</li>
        <li>Refundable Deposit Amt {deposit}/-</li>
        <li>Total Payable Amt : {firstMonthTotal}/-</li>
        <li>Renew Plan by Just Paying {renewal}/- Rs.</li>
      </ul>
    </div>
  );
};

export default PackageCard;
